import { chain, forEach, includes, map, some, toNumber, uniq } from 'lodash';
import {
  IComponent,
  IGcfComponentConfig,
  IGcfProductListGroups,
  IGcfProductListProduct,
  IGcfProductListProductSize,
  IGcfProductListProductSizeVariation,
  IProduct,
} from 'src/interfaces';
import { filterGcfProducts } from 'src/utils/gcf-helper';

export const getMatchingCategoryProducts = (
  products: IProduct[],
  categoryAdded: number[],
) => {
  const uniqueCategoryAdded = uniq(categoryAdded);

  return chain(products)
    .filter((product) => {
      const categoryIds = map(product.product_category_id, toNumber);

      return some(categoryIds, (value) => uniqueCategoryAdded.includes(value));
    })
    .value();
};

export const getUpdatedVariationProductCodes = (
  groups?: IGcfProductListGroups,
) => {
  if (!groups) {
    return [];
  }
  let updatedVariationProductCodes: any = [];

  forEach(groups, (group) => {
    forEach(group?.products, (product: IGcfProductListProduct) => {
      forEach(product?.sizes, (size: IGcfProductListProductSize) => {
        forEach(
          size?.variations,
          (variation: IGcfProductListProductSizeVariation) => {
            if (
              !includes(updatedVariationProductCodes, variation.productCode)
            ) {
              updatedVariationProductCodes.push(variation.productCode);
            }
          },
        );
      });
    });
  });

  return updatedVariationProductCodes;
};

export const getUpdatedComponentConfig = (
  products: IProduct[],
  categoryAdded: number[],
  tagsAdded: number[],
  componentDetails: IComponent,
  componentConfig: Partial<IGcfComponentConfig> | undefined,
  componentName: string,
  ooPermission: boolean,
  expiry?: string | null,
  startDate?: string | null,
) => {
  let variationProductCodes =
    componentDetails?.product_variation_product_code || [];

  if (categoryAdded.length > 0) {
    const uniqueCategoryAdded = uniq(categoryAdded);

    const filteredProduct = getMatchingCategoryProducts(
      products,
      uniqueCategoryAdded,
    );

    // A category restriction is applied: keep only products that match the
    // selected categories. When none match, every product must be removed —
    // pass keepAllWhenEmpty=false so an empty match doesn't retain products
    // that would fail the backend's category-overlap validation on save.
    const { productList, productVariationCodes } = filterGcfProducts(
      filteredProduct,
      componentConfig?.productList,
      false,
    );

    if (componentConfig?.productList?.groups && productList) {
      componentConfig.productList = {
        ...componentConfig.productList,
        ...productList,
      };
    }

    variationProductCodes = productVariationCodes;
  } else {
    variationProductCodes = [
      ...getUpdatedVariationProductCodes(componentConfig?.productList?.groups),
    ];
  }

  return {
    name: componentName,
    end_date: expiry,
    start_date: startDate,
    usable_as_override: ooPermission,
    component_type_id: componentDetails.component_type_id,
    layout_type_id: componentDetails.layout_type_id,
    product_variation_product_code: uniq(variationProductCodes),
    product_category_id: uniq(categoryAdded),
    component_tag_id: uniq(tagsAdded),
    theme_id: componentDetails.theme_id,
    theme: componentDetails.theme,
    config: {
      ...componentConfig,
    },
  };
};
